export const BOOKING_STATUS = {
  PENDING: 1,
  CONFIRMED: 2,
  CANCELLED: 3,
  REJECTED: 4,
} as const;

export const BOOKING_LIFECYCLE = {
  NEW: 1,
  ALLOCATED: 2,
  CHECKED_IN: 3,
  CHECKED_OUT: 4,
  RELEASED: 5,
} as const;

const BOOKING_STATUS_BY_CODE: Record<string, number> = BOOKING_STATUS;
const BOOKING_LIFECYCLE_BY_CODE: Record<string, number> = BOOKING_LIFECYCLE;

function normalizeCode(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}

function normalizeStatus(
  numericValue: unknown,
  codeValue: unknown,
  valuesByCode: Record<string, number>,
): number | null {
  for (const value of [numericValue, codeValue]) {
    if (value === null || value === undefined || value === '') continue;
    const numericStatus = Number(value);
    if (Number.isInteger(numericStatus) && numericStatus > 0) return numericStatus;
  }

  return valuesByCode[normalizeCode(codeValue)]
    ?? valuesByCode[normalizeCode(numericValue)]
    ?? null;
}

export function bookingStatusOf(booking: any): number | null {
  return normalizeStatus(
    booking?.bookingStatus,
    booking?.bookingStatusCode,
    BOOKING_STATUS_BY_CODE,
  );
}

export function bookingLifecycleOf(booking: any): number | null {
  return normalizeStatus(
    booking?.lcStatus,
    booking?.lcStatusCode,
    BOOKING_LIFECYCLE_BY_CODE,
  );
}

export function isPendingBooking(booking: any): boolean {
  return bookingStatusOf(booking) === BOOKING_STATUS.PENDING;
}

export function isConfirmedBooking(booking: any): boolean {
  return bookingStatusOf(booking) === BOOKING_STATUS.CONFIRMED;
}

export function isBookingLifecycleLocked(booking: any): boolean {
  const lifecycle = bookingLifecycleOf(booking);
  return lifecycle === BOOKING_LIFECYCLE.CHECKED_IN
    || lifecycle === BOOKING_LIFECYCLE.CHECKED_OUT
    || lifecycle === BOOKING_LIFECYCLE.RELEASED;
}

function canChangeConfirmedBooking(booking: any): boolean {
  return isConfirmedBooking(booking) && !isBookingLifecycleLocked(booking);
}

export function canCancelBooking(booking: any): boolean {
  return canChangeConfirmedBooking(booking);
}

export function canAllocateBooking(booking: any): boolean {
  return canChangeConfirmedBooking(booking);
}

export function canCheckInBooking(booking: any): boolean {
  return isConfirmedBooking(booking)
    && bookingLifecycleOf(booking) === BOOKING_LIFECYCLE.ALLOCATED;
}
